import React, { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../i18n';
import Wordmark from '../ui/Wordmark';
import SoundToggle from './SoundToggle';
import { useFormatCurrency } from '../../utils/currencyContext';
import { GameStatusBanner, HostCrown, SeatBetLine } from './GameParts';
import { ActionArea, UtilityRow } from './ActionArea';
import GameMenu from './GameMenu';
import BlindsUpClock from './BlindsUpClock';
import {
  computeTableMetrics, seatAngles, seatPosition,
  ACTION_SLOT_H, BANNER_SLOT_H,
} from '../../utils/tableLayout';
import { isFoldedThisHand, seatPresence } from '../../utils/playerLedger';
import { seatStatusLabel } from '../../utils/sitOutCopy';
import { isEligibleWinnerSeat } from '../../utils/tapWinner';

// The traditional poker-table layout: seats around an oval, the board and the
// pot in the middle, the viewer always at the bottom.
//
// Same design system, same components, same handlers as the list layout — only
// the arrangement differs. Everything below the oval (status banner, action
// buttons, host tools, feedback) is the SHARED ActionArea / UtilityRow, so the
// two layouts cannot drift apart on behaviour.
//
// Sizing rule: this screen never scrolls. The dock is laid out first and the
// oval takes whatever is left (see utils/tableLayout.js), which is why it
// survives a 360x640 Android as well as a 430x932 Pro Max.

// Measure the table area and re-measure on rotate / keyboard / toolbar changes.
function useMeasuredBox() {
  const ref = useRef(null);
  const [box, setBox] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const read = () => {
      const r = el.getBoundingClientRect();
      setBox((prev) =>
        Math.round(prev.width) === Math.round(r.width) && Math.round(prev.height) === Math.round(r.height)
          ? prev
          : { width: r.width, height: r.height }
      );
    };

    read();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      window.addEventListener('orientationchange', read);
      return () => {
        window.removeEventListener('resize', read);
        window.removeEventListener('orientationchange', read);
      };
    }

    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, box];
}

const STAGE_REVEAL = { FLOP: 3, TURN: 4, RIVER: 5, SHOWDOWN: 5 };

export default function TableLayout(view) {
  const formatCurrency = useFormatCurrency();
  const {
    game, settings, playersArray, currentPlayerId, currentPlayer, hostId,
    currentStage, handNumber, totalPot, currentPots, toCall,
    winnerIds, waitingForHost, roundActions, soundMuted = false, onToggleSound,
    onOpenBalances, onOpenFeedback, onLeaveTable, onSitOut,
    layout, onToggleLayout,
    players, tapWinnerMode, pickedWinnerIds = [], onToggleWinnerSeat,
    isDeclaringWinner,
  } = view;

  const [wrapRef, box] = useMeasuredBox();
  const metrics = useMemo(
    () => computeTableMetrics(box.width, box.height, playersArray.length),
    [box.width, box.height, playersArray.length]
  );

  const heroIndex = playersArray.findIndex((p) => p.id === currentPlayerId);
  const angles = useMemo(
    () => seatAngles(playersArray.length, heroIndex),
    [playersArray.length, heroIndex]
  );

  const { tableW, tableH, scale, seat, overflowing } = metrics;
  const revealed = STAGE_REVEAL[currentStage] || 0;
  const multiPot = (currentPots?.length ?? 0) > 1;
  const mainPot = currentPots?.find((p) => p.isMainPot)?.amount ?? currentPots?.[0]?.amount ?? 0;
  const sidePots = (currentPots || []).filter((p) => !p.isMainPot).reduce((s, p) => s + (p.amount ?? 0), 0);

  const px = (n) => `${n}px`;

  return (
    <>
      {/* Header — wordmark left, hand meta right. The board cards move into the
          middle of the table on this layout, so they are not repeated here. */}
      <div className="px-4 pt-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Wordmark size="sm" />
            {onToggleSound && <SoundToggle muted={soundMuted} onToggle={onToggleSound} />}
          </div>
          <div className="flex items-center gap-2.5 min-w-0">
            <BlindsUpClock settings={settings} />
            <span data-testid="hand-number" className="text-[11px] text-mute tracking-[0.09em] uppercase whitespace-nowrap">
              {t('game.handNumber', { n: handNumber })}
            </span>
            <GameMenu
              layout={layout}
              onToggleLayout={onToggleLayout}
              onOpenBalances={onOpenBalances}
              onOpenFeedback={onOpenFeedback}
              onLeaveTable={onLeaveTable}
              onSitOut={onSitOut}
              roundActions={roundActions}
            />
          </div>
        </div>
      </div>

      {/* The table. `overflowing` only trips on a viewport too short for even
          the minimum oval (landscape, mostly) — there we allow a scroll rather
          than clipping seats away. */}
      <div
        ref={wrapRef}
        data-testid="table-wrap"
        dir="ltr"
        className={`i18n-ltr flex-1 min-h-0 relative mx-4 mt-2 ${overflowing ? 'overflow-y-auto' : 'overflow-hidden'}`}
      >
        {/* Rail + surface. Two hairline ellipses, no felt green — this is the
            same cream/ink system as every other screen. */}
        <div
          aria-hidden="true"
          data-testid="table-oval"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-cream-2 border border-line"
          style={{ width: px(tableW), height: px(tableH) }}
        >
          <span className="absolute inset-[12px] rounded-[50%] border border-line-2 bg-cream" />
        </div>

        {/* Centre: board cards, pot, side-pot split, to-call */}
        <div
          data-testid="table-centre"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center z-[2]"
          style={{ width: px(Math.round(172 * scale)) }}
        >
          <div data-testid="board-cards" className="flex justify-center" style={{ gap: px(Math.round(6 * scale)), marginBottom: px(Math.round(13 * scale)) }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{ width: px(Math.round(20 * scale)), height: px(Math.round(27 * scale)) }}
                className={`rounded-[3px] border ${i < revealed ? 'bg-ink border-ink' : 'bg-transparent border-line'}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-center gap-1.5" style={{ height: px(Math.round(14 * scale)), marginBottom: px(Math.round(5 * scale)) }}>
            <span data-testid="pot-label" className="label-eyebrow" style={{ fontSize: px((10.5 * scale).toFixed(1)) }}>{t('poker.pot')}</span>
            {multiPot && (
              <span className="text-[9px] font-semibold tracking-[0.07em] px-[5px] py-[1.5px] rounded-[4px] border border-line text-mute bg-cream">
                {currentPots.length} {t('poker.potsCaps')}
              </span>
            )}
          </div>

          {/* w-fit: the collision box is the dollar amount, not the
              full 172px centre column. A block-level pot-total stole
              every inner-rail pocket on a short phone. */}
          <div
            className="font-medium tracking-[-0.035em] tnum leading-none mx-auto w-fit"
            style={{ fontSize: px(Math.round(37 * scale)) }}
            data-testid="pot-total"
          >
            {formatCurrency(totalPot)}
          </div>

          {/* Side-pot split line is always reserved (hidden with one pot) so a
              side pot appearing never shifts the board cards / pot. */}
          <div
            className="text-mute tnum leading-tight"
            style={{ fontSize: px((11.5 * scale).toFixed(1)), marginTop: px(Math.round(6 * scale)), visibility: multiPot ? 'visible' : 'hidden' }}
            aria-hidden={!multiPot}
          >
            {t('table.main')} {formatCurrency(mainPot)}
            <span className="text-line mx-1">·</span>
            {t('table.side')} {formatCurrency(sidePots)}
          </div>

          <div
            data-testid="to-call"
            className="text-mute font-medium tnum"
            style={{ fontSize: px((12.5 * scale).toFixed(1)), marginTop: px(Math.round(7 * scale)) }}
          >
            {toCall > 0 ? t('game.toCall', { amount: formatCurrency(toCall) }) : t('table.noBetToCall')}
          </div>
        </div>

        {/* Seats */}
        {playersArray.map((player, i) => {
          const picked = tapWinnerMode && pickedWinnerIds.includes(player.id);
          const pickable = tapWinnerMode && !isDeclaringWinner && isEligibleWinnerSeat(player);
          return (
            <Seat
              key={player.id}
              player={player}
              angle={angles[i]}
              tableW={tableW}
              tableH={tableH}
              seat={seat}
              isHero={player.id === currentPlayerId}
              isCurrentTurn={game?.currentTurnPlayerId === player.id && seatPresence(player) !== 'away' && seatPresence(player) !== 'next' && seatPresence(player) !== 'out'}
              isWinner={(winnerIds.includes(player.id) || picked) && !player.hasFolded && seatPresence(player) !== 'away' && seatPresence(player) !== 'next' && seatPresence(player) !== 'out'}
              isHostSeat={player.id === hostId}
              roundActions={roundActions}
              stage={game?.stage}
              picked={picked}
              onPick={pickable ? () => onToggleWinnerSeat?.(player.id) : undefined}
              handAwarded={winnerIds.length > 0}
              winnerInfo={game?.winnerInfo}
            />
          );
        })}
      </div>

      {/* Dock — laid out before the table gets its space, and held clear of the
          home indicator so Fold/Call/Raise is never on the very bottom edge.

          The action slot is reserved whether or not it is your turn. The dock
          is what decides how much room the oval gets, so a dock that changed
          height would resize the table mid-hand. History lives in the Menu. */}
      <div
        className="flex-shrink-0 px-4"
        style={{ paddingBottom: 'calc(var(--dock-pad, 26px) + env(safe-area-inset-bottom, 0px))' }}
      >
        <div
          data-testid="table-banner-row"
          className="flex flex-col justify-center"
          style={{ minHeight: px(BANNER_SLOT_H) }}
        >
          <GameStatusBanner
            game={game}
            players={players || {}}
            viewerId={currentPlayerId}
            hostId={hostId}
            tapWinnerMode={tapWinnerMode}
          />
        </div>

        {/* Reserved action slot. Content is centred in it, so the space between
            the status banner and the buttons is real breathing room rather than
            the buttons hanging off the banner. */}
        <div
          data-testid="table-action-slot"
          className="mt-3.5 flex flex-col justify-center gap-2"
          style={{ minHeight: px(ACTION_SLOT_H) }}
        >
          {waitingForHost && (currentPlayer?.stack || 0) > 0 && (
            <div className="flex items-center justify-center gap-2">
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#8a857d', display: 'inline-block', flexShrink: 0, animation: 'cl-pulse 1.6s ease-in-out infinite' }} />
              <span className="text-[12.5px] text-mute">{t('table.waitingDealNext')}</span>
            </div>
          )}

          <ActionArea {...view} />
        </div>

        <UtilityRow {...view} />
      </div>
    </>
  );
}

// One seat on the rail. Same tokens as PlayerCard — ink fill on turn/win,
// dashed hatch when folded, text role badges — just shaped for the oval.
function Seat({ player, angle, tableW, tableH, seat, isHero, isCurrentTurn, isWinner, isHostSeat, roundActions = [], stage, onPick, picked = false, handAwarded = false, winnerInfo }) {
  const formatCurrency = useFormatCurrency();
  const { x, y } = seatPosition(angle, tableW, tableH);
  const presence = seatPresence(player);
  const waitingLook = presence === 'away' || presence === 'next';
  const out = presence === 'out';
  const dark = !waitingLook && !out && (isCurrentTurn || isWinner || picked);
  // `micro` is set when computeTableMetrics had to shrink the cards to fit a
  // crowded oval (10-handed on a short phone). Type comes down with the box.
  const { micro } = seat;
  const dense = seat.dense || micro;
  const folded = !waitingLook && !out && isFoldedThisHand(player);
  const hatch = folded || out;

  const isDealer = player.role === 'DEALER' || player.role === 'DEALER_SMALL_BLIND';
  const isSmallBlind = player.role === 'SMALL_BLIND' || player.role === 'DEALER_SMALL_BLIND';
  const isBigBlind = player.role === 'BIG_BLIND';
  const roleText = out
    ? null
    : (presence === 'pending' || presence === 'away' || presence === 'next')
    ? seatStatusLabel(presence, { dense: true, micro })
    : isDealer && isSmallBlind ? t('poker.dSb') : isDealer ? t('poker.d') : isSmallBlind ? t('poker.sb') : isBigBlind ? t('poker.bb') : null;

  // Same pill on every seat size — micro only drops the type, not the
  // border / padding / radius. Absolute on the stack line so it never
  // sits on the name.
  const badgeCls = `${micro ? 'text-[7px] px-[2px]' : dense ? 'text-[8.5px] px-[4px]' : 'text-[9px] px-[4px]'} font-semibold tracking-[0.06em] leading-none py-[1px] rounded-[4px] border flex-shrink-0 ${
    dark ? 'border-white/30 text-white/85' : 'border-line text-mute'
  }`;

  const tone = dark
    ? 'bg-ink text-cream border-ink'
    : waitingLook
    ? 'border-dashed border-line bg-cream-2 text-mute'
    : hatch
    ? 'border-dashed border-line-2 text-mute cl-folded-hatch bg-cream'
    : `bg-cream text-ink ${isHero ? 'border-[#cfcac0]' : 'border-line'}`;

  return (
    <div
      data-seat-id={player.id}
      data-player-id={player.id}
      data-seat-offset={y}
      data-testid={`seat-${player.name}`}
      className={`absolute -translate-x-1/2 -translate-y-1/2 z-[4]${onPick ? ' cursor-pointer' : ''}`}
      style={{ left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)` }}
      {...(onPick ? {
        role: 'button',
        tabIndex: 0,
        'aria-pressed': !!picked,
        'data-pickable': 'true',
        onClick: onPick,
        onKeyDown: (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onPick();
          }
        },
      } : {})}
    >
      <div
        data-testid="seat-card"
        className={`relative overflow-hidden border transition-colors duration-200 ${
          micro ? 'rounded-[8px] px-1 py-[2px]' : dense ? 'rounded-[10px] px-2 py-[5px]' : 'rounded-[11px] px-[9px] py-[6px]'
        } ${tone} ${
          isCurrentTurn && !waitingLook ? 'shadow-[0_0_0_3px_rgba(13,13,13,0.11)]' : ''
        }`}
        style={{ width: `${seat.width}px` }}
      >
        <div className="flex items-center gap-1 min-w-0 overflow-hidden">
          <span
            data-testid={`seat-name-${player.name}`}
            className={`${micro ? 'text-[8px]' : dense ? 'text-[10px]' : 'text-[12.5px]'} tracking-[-0.03em] truncate leading-tight min-w-0 flex-1 ${
              folded ? 'font-medium text-mute line-through decoration-mute/70' : (waitingLook || out) ? 'font-medium text-mute' : 'font-semibold'
            }`}
          >
            {player.name}
          </span>
          {isHostSeat && <HostCrown />}
        </div>
        <div className={`min-w-0 h-[12px] overflow-hidden ${micro ? 'text-[8px]' : dense ? 'text-[10px]' : 'text-[11px]'} tnum leading-none ${dark ? 'opacity-75' : 'text-mute'}`}>
          <span data-testid={`stack-${player.name}`} className="block h-full whitespace-nowrap overflow-hidden">
            {formatCurrency(player.stack)}
          </span>
        </div>
        <div data-testid="seat-bet-row" className={`flex items-center gap-0.5 min-w-0 overflow-hidden ${
          micro ? 'h-[11px]' : 'h-[13px]'
        }`}>
          <div className="min-w-0 flex-1 overflow-hidden">
            <SeatBetLine
              player={player}
              roundActions={roundActions}
              stage={stage}
              formatCurrency={formatCurrency}
              size={micro ? 'micro' : dense ? 'dense' : 'table'}
              dark={dark}
              reserved
              handAwarded={handAwarded}
              winnerInfo={winnerInfo}
              testId={`bet-chip-${player.name}`}
            />
          </div>
          {roleText && (
            <span
              className={`${badgeCls} box-border h-full whitespace-nowrap flex items-center`}
              data-testid={presence === 'away' ? 'away-badge' : presence === 'pending' ? 'away-pending-badge' : waitingLook ? 'next-hand-badge' : 'seat-role'}
            >
              {roleText}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
